import { ID } from 'shared/types/id';
import { v4 as uuidv4 } from 'uuid';

export type WordId = {
  id: ID;
  word: string;
};

export const getIdsForWords = (
  sentences: string | string[],
  existingIds: WordId[] = [],
  masterRegex?: RegExp,
): WordId[] => {
  const text = Array.isArray(sentences) ? sentences.join(' ') : sentences;

  // If no master regex is provided, fall back to the original word-by-word behavior
  if (!masterRegex) {
    const words = text.split(/\s+/).filter(Boolean);
    return processWords(words, existingIds);
  }

  // Find all regex matches and their positions
  const matches = [...text.matchAll(masterRegex)];
  // eslint-disable-next-line no-console -- temp
  const segments: string[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    const matchIndex = match.index!;
    const matchLength = match[0].length;

    // Add text before the match as individual words
    if (matchIndex > lastIndex) {
      const beforeMatch = text.substring(lastIndex, matchIndex);
      const words = beforeMatch.split(/\s+/).filter(Boolean);
      segments.push(...words);
    }

    // Add the entire match as a single segment
    const fullMatch = text.substring(matchIndex, matchIndex + matchLength);
    segments.push(fullMatch);

    lastIndex = matchIndex + matchLength;
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    const afterLastMatch = text.substring(lastIndex);
    const words = afterLastMatch.split(/\s+/).filter(Boolean);
    segments.push(...words);
  }

  return processWords(segments, existingIds);
};

const processWords = (words: string[], existingIds: WordId[]): WordId[] => {
  // This is crucial for handling duplicate words correctly.
  const availableOldIds = [...existingIds];
  const result: WordId[] = [];

  for (const newWordText of words) {
    // Find the first available match in the old IDs pool (case-insensitive).
    const oldIdIndex = availableOldIds.findIndex(
      (oldId) => oldId.word.toLowerCase() === newWordText.toLowerCase(),
    );

    let id;
    if (oldIdIndex !== -1) {
      const [reusedWord] = availableOldIds.splice(oldIdIndex, 1);
      id = reusedWord.id;
    }
    id = id ?? uuidv4();
    result.push({ id, word: newWordText });
  }

  return result;
};
